/**
 * All resource stores — one per domain entity.
 *
 * Each store is a singleton created by createResourceStore.
 * Import whichever you need directly in components:
 *
 *   import { quoteStore } from 'src/store/stores';
 *   const { data: quotes, loading } = quoteStore.useStore();
 */

import { createResourceStore } from './createResourceStore';
import type { ResourceStore } from './createResourceStore';
import type { Business } from '../models/business';
import type { Contact } from '../models/contact';
import type { Quote } from '../models/quote';
import type { Event } from '../models/event';
import type { Communication } from '../models/communication';
import type { Document } from '../models/document';
import type { Group } from '../models/group';
import type { User } from '../models/user';
import type { Inventory } from '../models/inventory';
import type { PurchaseOrder } from '../models/purchaseOrder';
import type { Product } from '../models/products/product';
import type { ProductionOrder } from '../models/production/production_order';
import { ProductAddon } from '../models/commons/productAddon';
import { CommonData } from '../models/commons/commonData';

export const businessStore    = createResourceStore<Business>('/businesses');
export const contactStore     = createResourceStore<Contact>('/contacts');
export const quoteStore       = createResourceStore<Quote>('/quotes');
export const eventStore       = createResourceStore<Event>('/events');
export const communicationStore = createResourceStore<Communication>('/communications');
export const documentStore    = createResourceStore<Document>('/documents');
export const groupStore       = createResourceStore<Group>('/users/groups');
export const userStore        = createResourceStore<User>('/users');
export const inventoryStore   = createResourceStore<Inventory>('/inventory');
export const purchaseOrderStore = createResourceStore<PurchaseOrder>('/purchase-orders');
export const paintStore       = createResourceStore<Product>('/paints');
export const laboStore        = createResourceStore<Product>('/supplies');
export const productionOrderStore = createResourceStore<ProductionOrder>('/production-orders');

/**
 * Registry mapping WS resource names to stores.
 * Used by WsProvider to route incoming WebSocket events directly to the right store.
 */
export const storeRegistry: Record<string, ResourceStore<any>> = {
  BUSINESS: businessStore,
  CONTACT: contactStore,
  QUOTE: quoteStore,
  EVENT: eventStore,
  COMMUNICATION: communicationStore,
  DOCUMENT: documentStore,
  GROUP: groupStore,
  USER: userStore,
  INVENTORY: inventoryStore,
  PURCHASEORDER: purchaseOrderStore,
  PRODUCTION_ORDER: productionOrderStore,
  PAINT: paintStore,
  SUPPLY: laboStore,
};
